<!--
  - Copyright (c) 2024. Haodong JU
  -->

<template>
  <v-card
    v-if="show"
    class="pa-4"
    elevation="0"
  >
    <page-sub-header class="mb-6 fixed-header">
      <slot />
      <template #subtitle>
        <slot name="subtitle" />
      </template>
      <template
        v-if="!simpleForm"
        #action
      >
        <v-btn
          v-if="isDialog"
          class="mr-2"
          elevation="0"
          @click="closeDialog"
        >
          <v-icon left>
            mdi-close
          </v-icon>
          {{ $t('closed') }}
        </v-btn>
        <v-btn
          v-if="extraBtn"
          class="mr-2"
          elevation="0"
          @click="extraBtnFunction"
        >
          <v-icon left>
            {{ extraBtnIcon }}
          </v-icon>
          {{ $t(extraBtnName) }}
        </v-btn>
        <v-btn
          v-if="needJumpBtn"
          :disabled="!formValid || !canEdit"
          class="mr-2"
          color="blue lighten-4 black--text"
          elevation="0"
          @click="submitAndJump"
        >
          <v-icon left>
            mdi-content-save
          </v-icon>
          {{ $t('saveAndJump') }}
        </v-btn>
        <v-btn
          :disabled="!formValid || !canEdit"

          color="amber lighten-4 black--text"
          elevation="0"
          @click="submit"
        >
          <v-icon left>
            mdi-content-save
          </v-icon>
          {{ $t(saveBtnName) }}
        </v-btn>
      </template>
    </page-sub-header>
    <loading-provider :loading="loading">
      <v-form
        v-if="!reloading"
        ref="form"
        v-model="formValid"
        lazy-validation
      >
        <div>
          <div
            v-for="(f,index) in fields"
            :key="index"
          >
            <div class="d-flex">
              <div
                class="text-body-1 mr-2"
                style="width: 50%;"
              >
                <div class="font-weight-bold">
                  {{ $t(f.name) }}<span v-if="f.required">*</span>
                </div>
                <div class="text-body-2">
                  {{ $t(f.hint) }}
                </div>
              </div>
              <v-spacer />
              <div
                class="d-flex align-start justify-end"
                style="width: 50%;"
              >
                <component
                  :is="f.component"
                  v-model="holderObject[f.key]"
                  dense
                  v-bind="f.componentProps"
                />
                <v-icon
                  v-if="f.componentProps?.iconAppend"
                  class="ml-1 mt-2"
                  @click="f.componentProps?.iconAppend.onClick()"
                >
                  {{ f.componentProps.iconAppend.icon }}
                </v-icon>
              </div>
            </div>
          </div>
        </div>
      </v-form>
    </loading-provider>
    <template v-if="simpleForm">
      <v-btn
        :disabled="!formValid || !canEdit"

        color="amber lighten-4 black--text"
        elevation="0"
        width="100%"
        @click="submit"
      >
        <v-icon left>
          mdi-content-save
        </v-icon>
        {{ $t(saveBtnName) }}
      </v-btn>
    </template>
  </v-card>
</template>

<script>

import { mapSchemaToField } from '@/views/widget/INK/BaseForm/baseFormUtils'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'

export default {
  name: 'BaseForm',
  components: { LoadingProvider, PageSubHeader },
  props: {
    canEdit: {
      type: Boolean,
      default: true
    },
    isDialog: {
      type: Boolean,
      default: true
    },
    schema: {
      type: Array,
      default: () => []
    },
    editingObject: {
      type: Object,
      required: false,
      default: () => null
    },
    loading: {
      type: Boolean,
      default: false
    },
    show: {
      type: Boolean,
      default: true
    },
    modelValue: { // 添加 modelValue 作为 v-model 的绑定值
      type: Object,
      default: () => ({})
    },
    needJumpBtn: {
      type: Boolean,
      default: false
    },
    simpleForm: {
      type: Boolean,
      default: false
    },
    saveBtnName: {
      type: String,
      default: 'save'
    },
    extraBtn: {
      type: Boolean,
      default: false
    },
    extraBtnName: {
      type: String,
      default: 'Preview'
    },
    extraBtnIcon: {
      type: String,
      default: 'mdi-image-search'
    }
  },
  data: function () {
    return {
      reloading: false,
      formValid: null,
      holderObject: this.modelValue
    }
  },
  methods: {
    closeDialog () {
      this.$emit('close')
      this.reset()
    },
    extraBtnFunction () {
      this.$emit('extraBtnFunction')
    },
    submit () {
      if (this.$refs.form.validate()) {
        this.$emit('submit', this.holderObject)
      }
    },
    submitAndJump () {
      if (this.$refs.form.validate()) {
        this.$emit('submitAndJump', this.holderObject)
      }
    },
    reset () {
      this.reloading = true
      if (this.editingObject) {
        this.holderObject = Object.assign({}, this.editingObject)
      } else {
        this.holderObject = Object.fromEntries(this.schema.map(it => [it.key, it.default]))
      }
      this.reloading = false
    }
  },
  watch: {
    holderObject: {
      deep: true,
      handler (newVal) {
        this.$emit('holder-change', newVal)
      }
    },
    schema: {
      deep: true,
      immediate: true,
      handler (val) {
        this.reset()
      }
    },
    show (val) {
      if (val) {
        this.reset()
      }
    },
    editingObject: {
      deep: true,
      immediate: true,
      handler (val) {
        this.$nextTick(() => {
          this.reset()
        })
      }
    },
    modelValue (val) {
      this.holderObject = val
    }
  },
  computed: {
    fields () {
      return this.schema.map(mapSchemaToField)
    }
  }
}
</script>

<style scoped>
.fixed-header {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10;
}
</style>
